import { X } from 'lucide-react';

import { Button } from '@/components/core/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';

import { DataTableBooleanInput } from './data-table-boolean-input';
import { DataTableNumberInput } from './data-table-number-input';
import { DataTableSelectInput } from './data-table-select-input';
import {
  Filter,
  FilterDef,
  FilterDefs,
  FilterOperator,
  FilterVariant,
  SUPPORTED_OPERATORS,
  UnsafeFilter,
} from './types';

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'is',
  ne: 'is not',
  in: 'any of',
  notIn: 'none of',
  iLike: 'contains',
  notILike: 'does not contain',
  lt: 'less than',
  lte: 'less than or equal to',
  gt: 'greater than',
  gte: 'greater than or equal to',
};

function FilterValueInput({
  filter,
  filterDef,
  onChange,
}: {
  filter: Filter<FilterDefs, string>;
  filterDef: FilterDef<FilterVariant>;
  onChange: (filter: UnsafeFilter) => void;
}) {
  switch (filterDef.variant) {
    case 'multiselect':
    case 'select':
      return (
        <DataTableSelectInput
          filter={filter}
          filterDef={filterDef as FilterDef<'select' | 'multiselect'>}
          onChange={onChange}
        />
      );
    case 'number':
      return <DataTableNumberInput filter={filter} onChange={onChange} />;
    case 'boolean':
      return (
        <DataTableBooleanInput
          filter={filter}
          filterDef={filterDef as FilterDef<'boolean'>}
          onChange={onChange}
        />
      );
    default:
      return <span>todo</span>;
  }
}

type Props = {
  filter: Filter<FilterDefs, string>;
  filterDef: FilterDef<FilterVariant>;
  onChange: (filter: UnsafeFilter) => void;
  onRemove: (id: string) => void;
};

export function DataTableFilterItem({ filter, filterDef, onChange, onRemove }: Props) {
  return (
    <div
      key={filter.id}
      aria-label={`Filter for ${filterDef.label}`}
      role="listitem"
      className="flex h-8 items-center rounded-md bg-control"
    >
      {/* column name pill */}
      <div className="flex h-8 items-center gap-1.5 rounded-l-md border border-r-0 bg-control px-3 font-normal has-[>svg]:px-2.5">
        {filterDef.icon && <filterDef.icon className="h-4 w-4 text-muted-foreground" />}
        <span className="max-w-48 truncate text-sm">{filterDef.label}</span>
      </div>
      {/* Operator selector */}
      <Select
        value={filter.operator}
        onValueChange={(operator) => {
          onChange({ ...filter, operator });
        }}
      >
        <SelectTrigger
          aria-label={`Operator for ${filterDef.label}`}
          className="h-8 rounded-none border-r-0 px-2.5 text-sm [&_svg]:hidden [&[data-size]]:h-8"
        >
          <SelectValue placeholder={filter.operator} />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_OPERATORS[filterDef.variant].map((operator) => (
            <SelectItem key={operator} className="text-sm" value={operator}>
              {OPERATOR_LABELS[operator]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* filter value */}
      <FilterValueInput filter={filter} filterDef={filterDef} onChange={onChange} />
      {/* Close button */}
      <Button
        aria-label={`Remove filter for ${filterDef.label}`}
        variant="ghost"
        size="icon-sm"
        className="h-full rounded-none rounded-r-md border border-l-0 border-input bg-control px-1.5 text-sm font-normal"
        onClick={() => onRemove(filter.id)}
      >
        <X />
      </Button>
    </div>
  );
}
